import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/** The vector blueprints, as the API stores their ids. `custom` draws the walls only. */
export const TEMPLATE_IDS = [
  'standard-expo-hall',
  'jcc-assembly-hall',
  'modular-hall',
  'custom',
] as const
export type TemplateId = (typeof TEMPLATE_IDS)[number]

/** What the floor plan shows under a floor's stands when it opens. */
export const VENUE_DISPLAYS = ['blueprint', 'image', 'both', 'none'] as const
export type VenueDisplay = (typeof VENUE_DISPLAYS)[number]

export const ROOM_TYPES = [
  'exhibition',
  'stage',
  'fnb',
  'lounge',
  'service',
  'foyer',
  'void',
  'custom',
] as const
export type RoomType = (typeof ROOM_TYPES)[number]
export const DOOR_TYPES = ['entrance', 'exit'] as const
export const MARKER_TYPES = ['hydrant', 'stairs', 'escalator', 'atm'] as const
export type MarkerType = (typeof MARKER_TYPES)[number]

const roomSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(ROOM_TYPES),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
})
const doorSchema = z.object({
  id: z.string(),
  type: z.enum(DOOR_TYPES),
  label: z.string(),
  x: z.number(),
  y: z.number(),
  orientation: z.enum(['horizontal', 'vertical']),
})
const markerSchema = z.object({
  id: z.string(),
  type: z.enum(MARKER_TYPES),
  x: z.number(),
  y: z.number(),
  label: z.string().optional(),
})

/**
 * A floor's own layout, drawn over its template: rooms, doors and markers, in meters from the
 * floor's top-left corner. A door's and a marker's (x, y) is its center.
 */
export const blueprintSchema = z.object({
  rooms: z.array(roomSchema),
  doors: z.array(doorSchema),
  markers: z.array(markerSchema),
})
export type Blueprint = z.infer<typeof blueprintSchema>
export type BlueprintRoom = z.infer<typeof roomSchema>
export type BlueprintDoor = z.infer<typeof doorSchema>
export type BlueprintMarker = z.infer<typeof markerSchema>

export const EMPTY_BLUEPRINT: Blueprint = { rooms: [], doors: [], markers: [] }

export const floorSettingSchema = z.object({
  id: z.string(),
  /** The hall its stands carry (`booth.hall`). */
  name: z.string(),
  template: z.enum(TEMPLATE_IDS),
  widthM: z.number(),
  heightM: z.number(),
  imageKey: z.string().nullable(),
  /** Short-lived (five minutes): load it when it arrives, never store it. */
  imageUrl: z.string().nullable(),
  display: z.enum(VENUE_DISPLAYS),
  imageOpacity: z.number(),
  blueprint: blueprintSchema,
})
export type FloorSetting = z.infer<typeof floorSettingSchema>

export const venueSettingSchema = z.object({
  defaultFloorId: z.string(),
  floors: z.array(floorSettingSchema).min(1),
  updatedAt: z.string().nullable(),
})
export type VenueSetting = z.infer<typeof venueSettingSchema>

const venueResponseSchema = z.object({ venue: venueSettingSchema.nullable() })

/** The edition's saved venue, or null when it has not set one up. */
export function fetchVenue(signal?: AbortSignal): Promise<{ venue: VenueSetting | null }> {
  return api.get('/v1/exhibitor/venue', {
    schema: venueResponseSchema,
    ...(signal ? { signal } : {}),
  })
}

export type FloorInput = {
  /** A saved floor's id, or null for a new floor. */
  id: string | null
  name: string
  template: TemplateId
  widthM: number
  heightM: number
  /** A `venue-plan` upload key, or null for no image. */
  imageKey: string | null
  display: VenueDisplay
  /** Percent, 10 to 100. */
  imageOpacity: number
  blueprint: Blueprint
}

/** Every floor, in order. A saved floor left out is deleted, and its stands move to the default. */
export type VenueInput = { floors: FloorInput[]; defaultFloor: number }

export function saveVenue(input: VenueInput): Promise<VenueSetting> {
  return api.put('/v1/exhibitor/admin/venue', { schema: venueSettingSchema, body: input })
}
